import { Icon, Illustration } from "@storeblocks/icon";
import { AlertMargin, ThemedBorderSurface } from "App/global-style";
import { IconName, IllustrationName } from "Constants/constants";
import { isDefined, isEmpty } from "Utils/functions";
import { AlertReportInfo, useAlertReport } from "./alertHooks";
import { CardContent, CardHeading, DesktopBox } from "./styled";

import { Props as StoreblocksAlertProps } from "@storeblocks/alert";
import Box from "@storeblocks/box";
import { Props as SurfaceProps } from "@storeblocks/surface";
import { Icons } from "@storebrand-digital/insurance-definitions";
import { createKey } from "Utils/generalUtils";
import React from "react";

export interface BasicProps
  extends Omit<StoreblocksAlertProps, "variant">,
    Pick<SurfaceProps, "variant">,
    AlertReportInfo {
  marginBottom?: AlertMargin;
  flexDirection?: "row" | "row-reverse";
}

interface PropsWithIllustration extends BasicProps {
  illustrationName: IllustrationName;
  iconName?: never;
}

interface PropsWithIcon extends BasicProps {
  illustrationName?: never;
  iconName: IconName | Icons;
}

export type Props = PropsWithIllustration | PropsWithIcon;

export const AlertWithIllustration = ({
  variant = "border",
  title,
  description,
  marginBottom,
  illustrationName,
  iconName,
  children,
  flexDirection = "row",
  id,
  ...rest
}: Props): React.ReactElement => {
  useAlertReport({ title, description, ...rest });
  const uuid = createKey();

  const renderIcon = (): React.ReactElement | undefined => {
    if (isDefined(iconName)) {
      return <Icon name={iconName} width="96px" />;
    }
  };

  return (
    <ThemedBorderSurface
      variant={variant}
      id={id}
      minWidth="180px"
      marginBottom={marginBottom}
    >
      <Box padding="16px" flexDirection={flexDirection} flexGrow="1">
        <Box
          padding="10px"
          flexDirection="column"
          flexGrow="1"
          role="alert"
          aria-labelledby={`${uuid}-call-us-heading`}
          aria-describedby={`${uuid}-call-us-description`}
        >
          <CardHeading id={`${uuid}-call-us-heading`}>{title}</CardHeading>
          <CardContent
            id={`${uuid}-call-us-description`}
            flexDirection="column"
          >
            {!isEmpty(description) && <p>{description}</p>}
            {children}
          </CardContent>
        </Box>
        <DesktopBox flexShrink="0">
          {isDefined(illustrationName) && (
            <Illustration name={illustrationName} width="150px" />
          )}
          {isDefined(iconName) && renderIcon()}
        </DesktopBox>
      </Box>
    </ThemedBorderSurface>
  );
};
